import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  viewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CalendarCommonModule, CalendarEvent, CalendarMonthModule, DAYS_OF_WEEK } from 'angular-calendar';
import dayjs from 'dayjs';

import 'dayjs/locale/de';
import { Observable, ObservableInput, forkJoin, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { BasicStoreService } from 'src/app/services/BasicStore/basic-store.service';
import { SpinnerService } from 'src/app/services/Spinner/spinner.service';
import { MrTranslatePipe } from '../../pipes/mr-translate.pipe';
import { WartungsPlanNode as Wartungen } from '../_components/_overlays/wartungsverwaltung/wartung.types';
import { DetailviewComponent } from '../_components/detailview/detailview.component';
import { ProgressbarComponent } from '../_components/progressbar/progressbar.component';
import { ToDateDayjsPipe } from 'src/app/pipes/to-date-dayjs.pipe';
import { CommonModule } from '@angular/common';
import { ClarityModule } from '@clr/angular';
import { WartungApiService } from 'src/app/services/APIService/wartung.api.service';
import html2canvas from 'html2canvas';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { BegehungApiService } from 'src/app/services/APIService/begehung.api.service';
import { rxResource } from '@angular/core/rxjs-interop';
import { TABLE_EMPTY } from 'src/app/models/ui/table/table';
import { IconDgCellComponent } from '../_components/_shared/datagrid-cell/icon-dg-cell.component';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { ExcelService } from 'src/app/services/Shared/excel.service';
import { Row } from 'src/app/models/ui/table';
import { FILTER_TABLE_STORE, provideFilterStore } from 'src/app/shared/stores/filter-table.store';
import { ColumnFilterComponent } from '../_components/filter/column-filter/column-filter.component';
import { ifNotError } from 'src/app/misc/compositions';

interface Prüfungen {
  OSTAMMID: number;
  rechte: number;
  KPRUVID: number;
  icon: number;
  intervallbeginn: string; // DATE
  intervall: number;
  strBez: string;
  einheit: undefined;
}

interface Begehungen {
  OSTAMMID: number;
  rechte: number;
  icon: number;
  intervallbeginn: string; // DATE
  intervall: number;
  strBez: string;
  weekDay: DAYS_OF_WEEK;
  einheit: 'w';
}

type ForkJoinResponce = {
  [key in RevisionMode]: ObservableInput<RevisionArt[]>;
};

type RevisionMode = 'Wartungen' | 'Begehungen' | string;
type RevisionArt = Wartungen | Prüfungen | Begehungen;
type RevisionTermin = { eventDatum: string } & RevisionArt;
type GroupedEvents = {
  isOpened: boolean;
  events: CalendarEvent<RevisionTermin>[];
};

@Component({
  selector: 'app-kalender',
  templateUrl: './kalender.component.html',
  styleUrls: ['./kalender.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    CalendarCommonModule,
    CalendarMonthModule,
    ScrollingModule,
    DetailviewComponent,
    ProgressbarComponent,
    ColumnFilterComponent,
    IconDgCellComponent,
    MrTranslatePipe,
    ToDateDayjsPipe,
    HideIDColumnsPipe,
  ],
  providers: [provideFilterStore({ storage: 'kalender' })],
  encapsulation: ViewEncapsulation.None,
})
export class KalenderComponent implements OnInit, OnDestroy {
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly excelService = inject(ExcelService);

  private readonly wartungAPIService = inject(WartungApiService);
  private readonly begehungAPIService = inject(BegehungApiService);
  private readonly apiService = inject(APIService);
  private readonly basicStore = inject(BasicStoreService);
  protected readonly filterStore = inject(FILTER_TABLE_STORE);
  protected readonly spinner = inject(SpinnerService);
  protected readonly lang = inject(TranslateService).getCurrentLang();

  readonly columnFilters = viewChildren(ColumnFilterComponent);

  protected events$: Observable<CalendarEvent[]>;
  currentDate: Date;
  activeDayIsOpen: boolean = false;
  _ostammid: number = undefined;

  showprogressbar: boolean = false;
  showdetailview: boolean = false;
  hidedetailview: boolean = false;
  split50 = false;
  sidenavenlarged = false;
  progressbarenlarged = false;
  progressbars: any;
  indexScrub: number = -1;

  protected readonly isListeMode = signal(false);
  protected readonly listeResource = rxResource({
    params: () => this.isListeMode() || undefined,
    stream: () => this.apiService.getKalenderListe(1),
    defaultValue: TABLE_EMPTY(),
  });
  protected readonly selectedRow = signal<Row>(undefined);
  protected readonly pruefungsListe = ifNotError(this.listeResource, TABLE_EMPTY());
  protected kpruvid = signal<number>(undefined);
  protected rechte = 1;
  constructor() {
    this.filterStore.registerFilters(this.columnFilters);
    this.filterStore.registerTable(this.pruefungsListe);
    effect(() => {
      const selected = this.selectedRow();
      untracked(() => {
        if (selected == null) {
          this.showdetailview = false;
          return;
        }
        this._ostammid = +selected['OSTAMMID'];
        this.rechte = +selected['rechte'];
        this.showdetailview ||= true;
      });
    });

    dayjs.locale('de');
  }

  protected s_view: dayjs.Dayjs;
  protected e_view: dayjs.Dayjs;

  async ngOnInit() {
    this.currentDate = new Date();
    this.s_view = dayjs().startOf('month').startOf('day');
    this.e_view = dayjs(this.s_view).add(1, 'year');

    const {
      ostammid = null,
      showdetailview = null,
      events = null,
    } = this.basicStore.getComponentStore('kalender') ?? {};
    if (showdetailview) this.selectOstammid(ostammid);

    this.events$ =
      events ?
        of(events)
      : this.apiService.getKRPUV().pipe(
          switchMap(({ kpruv }) =>
            forkJoin<ForkJoinResponce>({
              Wartungen: this.wartungAPIService.getWartungsPlan('list'),
              Begehungen: this.begehungAPIService.getBegehungsCalendarList(),
              ...Object.fromEntries(kpruv.map(({ ID, Bezeichnung }) => [Bezeichnung, this.apiService.getKalender(ID)])),
            })
          ),

          map(revisionObjekt =>
            Object.entries(revisionObjekt).flatMap(([art, objekte]) =>
              this.groupEvents(this.findTermine(objekte), art as RevisionMode)
            )
          ),
          tap(events => this.basicStore.setComponentStore('kalender', { events }, false))
        );
  }

  private findTermine(objekte: RevisionArt[]): RevisionTermin[] {
    return objekte.flatMap(o => {
      const events: RevisionTermin[] = [];
      let einheit: 'M' | 'w' | 'd';
      switch (o.einheit) {
        case 'Tag':
          einheit = 'd';
          break;
        case 'w':
          einheit = 'w';
          break;
        default:
          einheit = 'M';
      }

      if (o.intervall <= 0) return [];

      for (
        let event_date = dayjs(o.intervallbeginn).startOf('day');
        event_date < this.e_view;
        event_date = dayjs(event_date).add(o.intervall, einheit)
      ) {
        if (event_date < this.s_view) continue;
        if ('weekDay' in o && typeof o.weekDay == 'number') {
          if (o.weekDay > event_date.day()) event_date = event_date.day(o.weekDay);
          else if (o.weekDay < event_date.day()) event_date = event_date.add(1, 'w').day(o.weekDay);
        } else if (event_date.day() == DAYS_OF_WEEK.SUNDAY) event_date = event_date.add(1, 'd');
        else if (event_date.day() == DAYS_OF_WEEK.SATURDAY) event_date = event_date.add(2, 'd');
        events.push({
          ...o,
          eventDatum: event_date.format(BackendLocaleDatePipe.format),
        });
      }
      return events;
    });
  }

  private groupEvents(events: RevisionTermin[], title: RevisionMode): CalendarEvent<GroupedEvents>[] {
    const groupedEvents = events.reduce(
      (groupedObj, event) => {
        const date = event.eventDatum;
        groupedObj[date] ??= { isOpened: false, events: [] };
        groupedObj[date].events.push({
          start: new Date(date),
          title: event.strBez,
          allDay: true,
          meta: event,
        });
        return groupedObj;
      },
      {} as { [datum: string]: GroupedEvents }
    );

    return Object.entries(groupedEvents).map(([datum, groupedEvents]) => ({
      start: new Date(datum),
      title,
      allDay: true,
      meta: groupedEvents,
    }));
  }

  capture() {
    html2canvas(document.querySelector('#capture')).then(canvas => {
      document.body.appendChild(canvas);
      var link = document.createElement('a');
      link.download = 'screenshot_' + dayjs().format('YYYY-MM-DDTHH:mm:ss') + '.png';
      link.href = canvas.toDataURL();
      link.click();
    });
  }

  excel() {
    this.excelService.exportAsExcelFile(this.pruefungsListe().rows, this.mrTranslate.transform('Prüfliste'));
  }

  ngOnDestroy() {
    this.basicStore.resetComponentStore(
      'kalender',
      {
        ostammid: this._ostammid,
        currentDate: this.currentDate,
        // * detailview Ansicht wird nicht wiedergestellt. Warum gespeichert?
        split50: this.split50,
        showdetailview: this.showdetailview,
        hidedetailview: this.hidedetailview,
        sidenavenlarged: this.sidenavenlarged,
        progressbarenlarged: this.progressbarenlarged,
      },
      false
    );
  }

  switchNextPrev(amount: any) {
    const newDate = dayjs(this.currentDate).add(amount, 'month');

    if (dayjs(newDate).isBefore(this.e_view, 'month') && !dayjs(newDate).isBefore(this.s_view, 'month')) {
      this.currentDate = newDate.toDate();
    } else {
      this.currentDate = dayjs(this.s_view).toDate();
    }
    this.activeDayIsOpen = false;
  }

  today() {
    this.currentDate = new Date();
  }

  handleEvent(
    event:
      | CalendarEvent<RevisionTermin>
      | {
          event: CalendarEvent<GroupedEvents>;
          all: CalendarEvent<GroupedEvents>[];
        }
  ) {
    if ('all' in event) {
      for (const group of event.all) {
        group.meta.isOpened = false;
      }
      event.event.meta.isOpened = true;
      this.currentDate = event.event.start;
      this.activeDayIsOpen = true;
    } else {
      this.rechte = event.meta.rechte;
      this.selectOstammid(event.meta.OSTAMMID);
      if ('KPRUVID' in event.meta) this.kpruvid.set(+event.meta.KPRUVID);
    }
  }

  selectOstammid(id: number) {
    if (id) {
      this._ostammid = id;
      this.showdetailview ||= true;
    }
  }

  reloadDetailview() {
    const reloadID = this._ostammid;
    this._ostammid = null;
    setTimeout(() => {
      this._ostammid = reloadID;
    });
  }

  dayClicked({ date, events }: { date: Date; events: CalendarEvent<GroupedEvents>[] }): void {
    if (dayjs(this.currentDate).isSame(date, 'month')) {
      if (dayjs(this.currentDate).isSame(date, 'day')) {
        this.activeDayIsOpen = !this.activeDayIsOpen;
      } else {
        this.currentDate = date;
        this.activeDayIsOpen = true;
      }
      events.forEach(e => (e.meta.isOpened = false));
    }
    if (events.length == 0) this.activeDayIsOpen = false;
  }

  enlargeSidenav(event) {
    if (event.mode == 'full') {
      this.sidenavenlarged = event.val;
    } else if (event.mode == 'half') {
      this.split50 = event.val;
    } else if (event.mode == 'progress') {
      this.showprogressbar = event.val;
      this.progressbars = {};
    } else if (event.mode == 'hidden') {
      this.hidedetailview = event.val;
    } else if (event.mode == 'close') {
      this.showdetailview = false;
      this.showprogressbar = false;
    }
  }

  handleScrub(event) {
    this.indexScrub = event;
  }

  showProgressData(data) {
    if (data) {
      this.progressbars = data;
      this.showprogressbar = true;
    }
  }

  enlargeProgressbar(val?) {
    if (val) {
      this.progressbarenlarged = true;
    } else {
      this.progressbarenlarged = !this.progressbarenlarged;
    }
  }
}
